import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Fire `/attendance/check-in` + best-effort `/auth/location`. Both are
// idempotent on the backend: check-in returns early when today's row
// already has a check_in, and /auth/location enriches the existing
// placeholder login row within a 10-min window (insert-fallback otherwise).
const captureDailyAttendance = async () => {
  try {
    const checkInRes = await api.post('/attendance/check-in');
    if (checkInRes.data?.success) {
      const { is_holiday, is_weekend, holiday_name } = checkInRes.data;
      if (is_holiday || is_weekend) {
        const reason = is_holiday ? `Today is a holiday (${holiday_name})` : 'Today is a weekend';
        localStorage.setItem('attendance_notice', reason);
      }
    }
  } catch (e) {
    console.warn('Auto check-in failed:', e);
  }

  if (navigator.geolocation) {
    const sendLocation = (pos, accuracyType) => {
      api.post('/auth/location', {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        accuracy_type: accuracyType,
      }).catch(() => {});
    };
    navigator.geolocation.getCurrentPosition(
      (pos) => sendLocation(pos, 'high'),
      () => {
        navigator.geolocation.getCurrentPosition(
          (pos) => sendLocation(pos, 'low'),
          () => {}, { enableHighAccuracy: false, timeout: 10000 }
        );
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  }
};

const todayIsoDate = () => new Date().toISOString().split('T')[0];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data.success) {
        // Keep wildcard `*` as-is — hasPermission() handles it explicitly.
        // Expanding it client-side would silently fall behind every time a
        // new permission key is introduced on the backend (e.g. smb.inbox).
        setUser(response.data.data);

        // Session-resume daily check-in. Most employees never log out — they
        // just close the tab. Without this, the next day's session-resume
        // produces no check-in row and the auto-mark-absent cron flags them
        // absent. Gated to once-per-day-per-device to avoid spamming the
        // endpoints on every page load.
        const last = localStorage.getItem('last_resume_check_in_date');
        const today = todayIsoDate();
        if (last !== today) {
          localStorage.setItem('last_resume_check_in_date', today);
          captureDailyAttendance();
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.data.success) {
        const { access_token, refresh_token, user: userData } = response.data.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        setToken(access_token);
        // Keep wildcard `*` as-is — hasPermission() handles it explicitly.
        setUser(userData);

        // Fresh login always re-triggers check-in + geolocation capture so
        // the backend `/auth/login` placeholder login_locations row gets
        // enriched with geo within the 10-min window. Also marks the daily
        // resume flag so the subsequent fetchUser cycle doesn't duplicate.
        localStorage.setItem('last_resume_check_in_date', todayIsoDate());
        captureDailyAttendance();

        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = async () => {
    // Auto check-out on logout
    try {
      await api.post('/attendance/check-out');
    } catch (e) {
      console.warn('Auto check-out failed:', e);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('attendance_notice');
    localStorage.removeItem('last_resume_check_in_date');
    setToken(null);
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.permissions?.includes('*')) return true;
    return user.permissions?.includes(permission) || false;
  };

  const hasRole = (roleSlug) => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === roleSlug);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      hasPermission,
      hasRole,
      refreshUser: fetchUser,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
